import React from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table';
import { createBrandColumns } from './BrandTableColumns';
import ProductPagination from '../ProductPagination';

const BrandTable = ({ 
    brands, 
    loading,
    pagination, 
    onPageChange, 
    onEdit 
}) => {
    // React Table 컬럼 정의
    const columns = React.useMemo(() => {
        if (!onEdit) {
        console.warn('BrandTable: onEdit prop is missing');
        }
        return createBrandColumns(onEdit || (() => {}));
    }, [onEdit]);

    // 디버깅: 데이터와 컬럼 확인
    console.log('BrandTable render - brands:', brands);
    console.log('BrandTable render - columns:', columns);
    console.log('BrandTable render - loading:', loading);

    // React Table 설정
    const table = useReactTable({
        data: brands || [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        manualPagination: true, // 서버 사이드 페이지네이션 사용
        pageCount: pagination.pages,
        columnResizeMode: 'onChange',
        enableColumnResizing: true,
        state: {
        pagination: {
            pageIndex: pagination.page - 1, // API는 1부터 시작, React Table은 0부터 시작
            pageSize: pagination.size,
        },
        },
        onPaginationChange: (updater) => {
        const newPagination = typeof updater === 'function' 
            ? updater({ pageIndex: pagination.page - 1, pageSize: pagination.size })
            : updater;
        
        // 페이지 변경 시 API 호출
        onPageChange(newPagination.pageIndex + 1); // API는 1부터 시작
        },
    });

    if (loading) {
        return (
        <div className="flex justify-center items-center py-10">
            <div className="text-gray-600">로딩 중...</div>
        </div>
        );
    }

    return (
        <div className="mt-5 overflow-x-auto">
        <table className="w-full border-collapse table-fixed min-w-full">
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                    <th 
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className={`bg-gray-50 px-3 py-3 text-left border-b-2 border-gray-200 text-gray-800 text-sm cursor-pointer hover:bg-gray-100 ${header.column.columnDef.meta?.className || ''}`}
                    style={{ width: header.getSize() }}
                    >
                    {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                        )}
                    {{
                        asc: ' 🔼',
                        desc: ' 🔽',
                    }[header.column.getIsSorted()] ?? null}
                    </th>
                ))}
                </tr>
            ))}
            </thead>
            <tbody>
            {!brands || brands.length === 0 ? (
                <tr>
                <td colSpan={table.getHeaderGroups()[0]?.headers.length || 4} className="text-center py-10 text-gray-500">
                    브랜드 데이터가 없습니다.
                </td>
                </tr>
            ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                <td colSpan={table.getHeaderGroups()[0]?.headers.length || 4} className="text-center py-10 text-gray-500">
                    표시할 브랜드가 없습니다.
                </td>
                </tr>
            ) : (
                table.getRowModel().rows.map(row => (
                <tr 
                    key={row.id}
                    className="cursor-pointer transition-colors bg-gray-100 hover:bg-gray-300"
                >
                    {row.getVisibleCells().map(cell => (
                    <td 
                        key={cell.id} 
                        className={`px-3 py-2 border-b border-gray-200 text-sm ${cell.column.columnDef.meta?.className || ''}`}
                        style={{ width: cell.column.getSize() }}
                        onClick={(e) => {
                        // 버튼 클릭 시에는 이벤트 전파 중지
                        if (cell.column.id === 'actions') {
                            e.stopPropagation();
                        }
                        }}
                    >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                    ))}
                </tr>
                ))
            )}
            </tbody>
        </table>

        {/* 페이지네이션 */}
        {!loading && brands && brands.length > 0 && (
            <ProductPagination
            pagination={pagination}
            onPageChange={onPageChange}
            />
        )}
        </div>
    );
};

export default BrandTable;

